import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Info, CheckCircle, AlertTriangle, AlertCircle, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface WelcomeMessagePreviewProps {
  title: string;
  body: string;
  style: 'info' | 'success' | 'warning' | 'important';
  displayMode: 'always' | 'dismissable';
}

const styleConfig = {
  info: {
    accent: 'bg-blue-500',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    Icon: Info,
  },
  success: {
    accent: 'bg-green-500',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    Icon: CheckCircle,
  },
  warning: {
    accent: 'bg-amber-500',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    Icon: AlertTriangle,
  },
  important: {
    accent: 'bg-red-500',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    Icon: AlertCircle,
  },
};

export function WelcomeMessagePreview({ title, body, style, displayMode }: WelcomeMessagePreviewProps) {
  const config = styleConfig[style];
  const Icon = config.Icon;

  return (
    <div className="border rounded-lg bg-background shadow-lg overflow-hidden">
      {/* Header */}
      <div className="px-4 py-2 bg-muted/50 border-b flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">Modal Preview</span>
        <div className="w-3 h-3 rounded-full bg-muted-foreground/30" />
      </div>

      {/* Modal content */}
      <div className="p-0 overflow-hidden">
        {/* Accent bar */}
        <div className={cn('h-1.5 w-full', config.accent)} />
        
        <div className="p-4">
          {/* Header */}
          <div className="flex items-start gap-3 pb-3">
            <div className={cn('rounded-full p-2', config.iconBg)}>
              <Icon className={cn('h-4 w-4', config.iconColor)} />
            </div>
            <div className="flex-1 pt-0.5">
              <h3 className="text-base font-semibold leading-tight">
                {title || 'Message Title'}
              </h3>
            </div>
          </div>

          {/* Body */}
          <div className="py-2 pl-10">
            <div className="prose prose-sm max-w-none text-muted-foreground prose-headings:text-foreground prose-a:text-primary prose-strong:text-foreground text-sm">
              {body ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {body}
                </ReactMarkdown>
              ) : (
                <p className="text-muted-foreground/50 italic">
                  Message body will appear here...
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="pt-3 mt-3 border-t flex justify-end gap-2">
            {displayMode === 'dismissable' ? (
              <>
                <Button variant="outline" size="sm" disabled>
                  Remind me later
                </Button>
                <Button size="sm" disabled>
                  Got it, don't show again
                </Button>
              </>
            ) : (
              <Button size="sm" disabled>
                Close
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
